import type { FC, ReactNode } from 'react';

import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';
import { isBefore } from 'date-fns';
import { isNil } from 'lodash-es';

import { pdfSegmentRangeThresholdDate } from '@iconik-io/shared-lib/src/react/components/shared/segment/constants';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import {
  selectAssetOriginalFormatFirstMetadata,
  selectAssetProxyTimecodeOptions,
  selectIsTimeBasedAsset,
} from '../../ducks/pages/asset/selectors';
import { useAppSelector } from '../../store/react_redux_typed';
import IconButton from '../../ui/icon_button/icon_button';
import type { TimecodeRenderer } from '../fields/timecode/renderers';
import { Timecode } from '../fields/timecode/timecode';

import styles from './segment.module.css';

type Props = {
  assetId: string;
  segment: SegmentEntityWithIdNormalized;
  inline?: boolean;
};

const pdfRenderer: TimecodeRenderer = (_props, state): ReactNode => {
  return <>Page {state.value * 1000 + 1}</>;
};

const pdfLegacyRenderer: TimecodeRenderer = (_props, state): ReactNode => {
  return <>Page {state.value}</>;
};

export const SegmentTimecode: FC<Props> = ({ assetId, segment, inline }) => {
  const { versionId } = useParams({ strict: false });
  const timeBased = useAppSelector((state) =>
    selectIsTimeBasedAsset(state, assetId, versionId),
  );
  const timecodeOptions = useAppSelector((state) =>
    selectAssetProxyTimecodeOptions(state, assetId, versionId),
  );
  const { store: unifiedPlayerStore } = useBasePlayerContext();
  const originalMetadata = useAppSelector((state) =>
    selectAssetOriginalFormatFirstMetadata(state, assetId, versionId),
  );
  const isPdf = originalMetadata?.format === 'PDF';
  const handlePlayClick = () => {
    unifiedPlayerStore.segment.setSelectedSegmentIds([segment.id]);
    unifiedPlayerStore.playableMedia.play();
  };

  if (!timecodeOptions) {
    return null;
  }

  let timeStart = segment.time_start_milliseconds;
  let timeEnd = segment.time_end_milliseconds;
  if (!timeStart && timeBased && !segment.parent_id) {
    timeStart = 0;
  }

  if (!timeEnd && timeBased && !segment.parent_id) {
    timeEnd = 0;
  }

  if (
    isNil(segment.time_start_milliseconds) &&
    isNil(segment.time_end_milliseconds)
  ) {
    return null;
  }

  // Beta PDF pages hack, see pdfSegmentRangeThresholdDate definition for more details.
  const resolvedPdfRenderer =
    segment.date_created &&
    !isNil(timeStart) &&
    isBefore(new Date(segment.date_created), pdfSegmentRangeThresholdDate) &&
    timeStart >= 1000
      ? pdfLegacyRenderer
      : pdfRenderer;

  return (
    <div
      className={classNames(styles.timecode_buttons, {
        [styles.inline]: inline,
      })}
      aria-label="Segment timecode"
    >
      {timeStart !== timeEnd ? (
        <IconButton
          type="button"
          iconName="player_play"
          iconSizeUAD="2xs"
          className={styles.play_button}
          onClick={handlePlayClick}
        />
      ) : null}
      <button
        type="button"
        data-segment-handler={segment.id}
        data-segment-start={timeStart}
        data-segment-end={timeEnd}
        className={styles.seek_button}
      >
        <Timecode
          timecodeOptions={timecodeOptions}
          value={timeStart ?? undefined}
          renderer={isPdf ? resolvedPdfRenderer : undefined}
        />
        {timeStart !== timeEnd && (
          <>
            -
            <Timecode
              timecodeOptions={timecodeOptions}
              value={Number(timeEnd)}
              renderer={isPdf ? resolvedPdfRenderer : undefined}
            />
          </>
        )}
      </button>
    </div>
  );
};
